import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Map } from "immutable";
import { Shortlisted } from "../../templates/Shortlisted";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  FETCH_APPLICANTS_REQUEST,
  UPDATE_APPLICANT_REQUEST,
  SEARCH_APPLICANT_REQUEST,
  FETCH_APPLICANTS_LIST_REQUEST,
} from "../../../redux/applicant/actionTypes";
import {
  updateApplicantRequest,
  resetIsUpdateSuccessful,
  fetchApplicantsListRequest,
  fetchTotalApplicantsRequest,
  updateTotalNewApplicants,
} from "../../../redux/applicant/actions";
import { clearSpecificRequest } from "../../../redux/request/action";
import { setSideNav } from "../../../redux/recruiter/actions";
import { FirebaseApplicant } from "../../../core/entities/applicant";
import { arrayToObject } from "../../../utils/helpers/transform";
import { Alert } from "../../atoms/Alert";

const ShortlistedPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location: any = useLocation();

  const [applicantsData, setApplicantsData] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastElement, setLastElement] = useState(undefined);

  const applicantsList = useSelector(
    (state: RootStateOrAny) =>
      state.requestStore?.[FETCH_APPLICANTS_LIST_REQUEST]
  );

  const totalApplicants = useSelector(
    (state: RootStateOrAny) => state.applicant.totalApplicants
  );

  const updateRequest = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[UPDATE_APPLICANT_REQUEST]
  );

  const isUpdating = useSelector(
    (state: RootStateOrAny) => state.applicant.isUpdating
  );

  useEffect(() => {
    if (location.state?.applicantId) {
      if (location.state?.fromSearch) {
        return history.replace(
          `/recruit/shortlisted/${location.state?.applicantId}/true`
        );
      }
      history.push(`/recruit/shortlisted/${location.state?.applicantId}`);
    }
  }, [location, history]);

  const dispatchFetchShortlisted = useCallback(
    (
      lastElementDocs?: unknown,
      limit = 10,
      subsStatus = undefined,
      position = undefined
    ) =>
      dispatch(
        fetchApplicantsListRequest({
          where: [
            ["applicationStatus.currentStatus", "==", "Shortlisted"],
            ["applicationStatus.subStatus", "in", subsStatus],
            ["positionApplied", "in", position],
            ["isDeleted", "==", false],
          ],
          orderBy: [["createdAt", "desc"]],
          limit,
          startAfter: lastElementDocs,
        })
      ),
    [dispatch]
  );

  useEffect(() => {
    dispatch(clearSpecificRequest(SEARCH_APPLICANT_REQUEST));
    dispatch(dispatchFetchShortlisted());
    dispatch(fetchTotalApplicantsRequest());
    return () => {
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_REQUEST));
      dispatch(clearSpecificRequest(UPDATE_APPLICANT_REQUEST));
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_LIST_REQUEST));
      dispatch(resetIsUpdateSuccessful());
    };
  }, [dispatch]);

  useEffect(() => {}, []);

  useEffect(() => {
    if (applicantsList && applicantsList?.response) {
      const lists = applicantsList.response.data;
      const whereQuery = applicantsList.response.query.where;
      const arrToObj = Map(arrayToObject(lists));
      if (whereQuery[0][2] === "Shortlisted") {
        const filterApplicantsData: any = Object.values(applicantsData).filter(
          (values: any) =>
            values.applicationStatus.currentStatus === "Shortlisted"
        );
        const mapApplicantsData = Map(arrayToObject(filterApplicantsData));
        const newShortlistedApplicantsList =
          mapApplicantsData.mergeDeep(arrToObj);
        setApplicantsData(newShortlistedApplicantsList.toJS());
        setLastElement(applicantsList.response.lastElement);
      }
    }
  }, [applicantsList]);

  useEffect(() => {
    location.pathname.includes("/recruit/shortlisted") &&
      dispatch(setSideNav(true));
  }, [dispatch, location.pathname]);

  const dispatchUpdateApplicant = useCallback(
    (id: string, params: Partial<FirebaseApplicant>) =>
      dispatch(updateApplicantRequest(id, params)),
    [dispatch]
  );

  const dispatchUpdateTotalNewApplicants = useCallback(
    (currentPhase: string, prevPhase: string, currentStatus: string) =>
      dispatch(
        updateTotalNewApplicants(currentPhase, prevPhase, currentStatus)
      ),
    [dispatch]
  );

  const onClickApplicant = (key: any) => {
    history.push(`/recruit/shortlisted/${key}`);
  };

  const handleOnChange = (pagination: any, filters: any) => {
    const { current: page, pageSize: currPageSize } = pagination;
    const filteredPosition = filters.position || undefined;
    const filteredStatus = filters.status || undefined;
    dispatch(fetchTotalApplicantsRequest());
    if (filters.position || filters.status) {
      setCurrentPage(1);
    }

    setPageSize(currPageSize);
    if (pageSize === currPageSize) {
      if (page > 1) {
        if (currentPage + 1 >= page) {
          dispatch(
            dispatchFetchShortlisted(
              lastElement,
              currPageSize,
              filteredStatus,
              filteredPosition
            )
          );
        } else {
          dispatch(
            dispatchFetchShortlisted(
              undefined,
              page * currPageSize,
              filteredStatus,
              filteredPosition
            )
          );
        }
      } else {
        dispatch(
          dispatchFetchShortlisted(
            undefined,
            currPageSize,
            filteredStatus,
            filteredPosition
          )
        );
      }
      setCurrentPage(page);
    } else {
      setCurrentPage(1);
      dispatch(
        dispatchFetchShortlisted(
          undefined,
          page * currPageSize,
          filteredStatus,
          filteredPosition
        )
      );
    }
  };

  const mappedApplicantsData = Object.values(applicantsData)
    .map((applicant: any, index: number) => {
      const status =
        !applicant.applicationStatus?.subStatus ||
        applicant.applicationStatus?.subStatus === "Read" ||
        applicant.applicationStatus?.subStatus === "Unread"
          ? ""
          : applicant.applicationStatus?.subStatus;
      const applicantName = applicant.applicantName
        ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
        : applicant.fullName;
      return {
        key: applicant.id,
        fullName: applicantName,
        position: applicant.positionApplied,
        contactNumber: applicant.contactNumber,
        email: applicant.email,
        dateApplied: `${moment(applicant?.createdAt?.toDate()).format(
          "MM/DD/YYYY hh:mm a"
        )}`,
        phase: {
          status: status || "Unassigned",
        },
        referredBy: applicant.referredBy,
      };
    })
    .sort((a: any, b: any) => {
      const date1: any = new Date(a.dateApplied);
      const date2: any = new Date(b.dateApplied);
      return date2 - date1;
    });

  const total = totalApplicants?.shortlisted?.current || 0;

  const currentStatus =
    updateRequest?.response?.["applicationStatus.currentStatus"];
  const subStatus = updateRequest?.response?.["applicationStatus.subStatus"];

  return (
    <>
      <Shortlisted
        tableData={mappedApplicantsData}
        onClickApplicant={onClickApplicant}
        isLoading={applicantsList?.isLoading}
        onUpdateApplicant={dispatchUpdateApplicant}
        isUpdating={isUpdating}
        error={updateRequest?.error}
        onChange={handleOnChange}
        total={total}
        currentPage={currentPage}
        updateTotalNewApplicants={dispatchUpdateTotalNewApplicants}
      />
      {updateRequest?.didSucceed &&
        (currentStatus === "Failed" || subStatus === "Declined") && (
          <Alert
            type="success"
            message={
              currentStatus === "Failed"
                ? "Applicant moved to failed status"
                : "Successfully moved applicant’s status"
            }
            showIcon
          />
        )}
      {updateRequest?.error && (
        <Alert type="error" message={updateRequest?.error} showIcon />
      )}
    </>
  );
};

export default ShortlistedPage;
